import { useCallback, useState } from 'react';

import { useSearchOrders } from '@frontend/api';

import { ORDER_STATUS, ORDER_STATUS_LABEL } from 'constants/all';

import ExpandableFilterList from '../components/ExpandableFilterList';
import OrderMatchCell from '../components/OrderMatchCell';
import SearchResultList from '../components/SearchResultList';
import Searching from '../components/Searching';
import StatusFilter from '../components/StatusFilter';
import { TABS } from '../constants';
import { useSearchDataContext } from '../context';
import {
  buildFilterSeeMore,
  buildFilterUpdate,
} from '../utils/buildFilterUpdate';

const ORDER_STATUSES = Object.values(ORDER_STATUS).filter(
  (orderStatus) => orderStatus !== 'all',
);

const ALWAYS_VISIBLE_FIELDS = [
  'number',
  'accountName',
  'contactFullName',
  'contactEmail',
  'confirmationEmail',
  'totalPrice',
  'purchaseOrderNumber',
];

const BILLING_ADDRESS_FIELDS = [
  'billingBusinessName',
  'billingFullName',
  'billingPhone',
  'billingAddress1',
  'billingAddress2',
  'billingCity',
  'billingState',
  'billingCountry',
  'billingPostalCode',
];

const PRICING_FIELDS = [
  'totalPrice',
  'netPayout',
  'expeditesFee',
  'shippingCost',
  'subtotal',
  'taxCost',
];

const SHIPPING_ADDRESS_FIELDS = [
  'shippingBusinessName',
  'shippingFullName',
  'shippingPhone',
  'shippingAddress1',
  'shippingAddress2',
  'shippingCity',
  'shippingState',
  'shippingCountry',
  'shippingPostalCode',
];

const OrderResultList = () => {
  const { query, userHasTyped, activeTab } = useSearchDataContext();

  const [paginateFrom, setPaginateFrom] = useState(query.quotes.paginateFrom);

  const [filters, setFilters] = useState(query.quotes.filters);

  const { data: orders } = useSearchOrders(
    {
      ...query.orders,
      paginateFrom,
      filters,
    },
    {
      queryOptions: {
        keepPreviousData: true,
        enabled: activeTab === TABS.ORDERS,
      },
    },
  );

  const onStatusChange = useCallback((status) => {
    setFilters((filters) => {
      const selectedStatuses = filters.status;
      return {
        ...filters,
        status: selectedStatuses.includes(status)
          ? selectedStatuses.filter((old) => old !== status)
          : [...selectedStatuses, status],
      };
    });
  }, []);

  const onAccountNameChange = useCallback(
    (accountName) =>
      buildFilterUpdate(
        setFilters,
        'accountNames',
        'accountNames',
      )(accountName),
    [],
  );

  const onAccountNameSeeMore = useCallback(
    () => buildFilterSeeMore(setFilters, 'accountNames')(),
    [],
  );
  if (userHasTyped) {
    return <Searching />;
  }

  return (
    <SearchResultList
      data={orders}
      tabKey={TABS.ORDERS}
      removeMatchCellPadding
      labelPlural="Orders"
      labelSingular="Order"
      matchCell={(result) => <OrderMatchCell orderResult={result} />}
      highlightConfig={{
        alwaysVisibleFields: ALWAYS_VISIBLE_FIELDS.concat(
          BILLING_ADDRESS_FIELDS,
          PRICING_FIELDS,
          SHIPPING_ADDRESS_FIELDS,
        ),
      }}
      filters={
        <>
          <StatusFilter
            possibleStatuses={ORDER_STATUSES}
            statusLabels={ORDER_STATUS_LABEL}
            data={orders}
            selectedStatuses={filters.status}
            onChange={onStatusChange}
          />
          <ExpandableFilterList
            agg={orders?.aggs.accountNamesAgg.accountNames}
            label="Account Names"
            selectedKeys={filters.accountNames.accountNames}
            onClick={onAccountNameChange}
            onSeeMore={onAccountNameSeeMore}
          />
        </>
      }
      paginationInfo={{ paginateFrom, paginateSize: query.orders.paginateSize }}
      onPaginate={(pagination) => setPaginateFrom(pagination.paginateFrom)}
    />
  );
};

export default OrderResultList;
